import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { getProductDetailApiV1ProductsOrgIdProductsProductIdGet } from '@/common/api/client/sdk.gen'
import { useCurrentOrg } from '../../org/model'

const PRODUCT_TERMINAL_STATUS = ['synced', 'sync_error']
const PRODUCT_POLL_INTERVAL_MS = 2000

const isTerminalStatus = (status: string | undefined, terminal: readonly string[]): boolean => {
    if (!status) {
        return false
    }
    return terminal.includes(status)
}

const getErrorStatus = (err: unknown): number | undefined => {
    if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as { response?: { status?: number } }).response
        return response?.status
    }
    return undefined
}

export const useProduct = () => {
    const { data: currentOrg, isLoading: currentOrgLoading } = useCurrentOrg()
    const { productId } = useParams()

    const {
        data: product,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['product', currentOrg?.id, productId],
        queryFn: () =>
            getProductDetailApiV1ProductsOrgIdProductsProductIdGet({
                path: { org_id: currentOrg?.id ?? '', product_id: productId ?? '' },
            }).then(r => r.data!),
        refetchInterval: query => {
            if (query.state.error) {
                return false
            }
            const status = query.state.data?.status
            if (isTerminalStatus(status, PRODUCT_TERMINAL_STATUS)) {
                return false
            }
            return PRODUCT_POLL_INTERVAL_MS
        },
        retry: (failureCount, err) => {
            const status = getErrorStatus(err)
            if (status && status >= 400 && status < 500) {
                return false
            }
            return failureCount < 3
        },
        enabled: !!currentOrg,
    })

    const isProductSyncPolling = Boolean(product) && !isTerminalStatus(product?.status, PRODUCT_TERMINAL_STATUS)

    return {
        product,
        currentOrg,
        isLoading: currentOrgLoading || isLoading || isProductSyncPolling,
        error,
    }
}
